import React from 'react';
import { Tag, Space, Divider } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPhone, faCalendarAlt, faClock, faEye, faEdit, faBox } from '@fortawesome/free-solid-svg-icons';
import {
  OrderCard as StyledOrderCard,
  OrderHeaderRow,
  OrderInfoLeft,
  OrderMeta,
  OrderAmounts,
  OrderActionsBlock,
  StyledButton,
  ItemCard,
  CustomizationRow,
  SectionTitle,
  OrderItemsWrap,
  ItemCardInner,
  CustomizationsWrap,
  EmptyCustomizationsWrap,
  PhoneBlurred,
  AmountValueTotal,
} from './styles';
import {
  addCommasInPrices,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  formatDateAndTime,
  getPhoneBlurred,
  getPhoneUnBlurred,
  computeOrderSubtotal,
  computeOrderTax,
} from './constants';
import type { OrderCardProps } from './types';

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  isExpanded,
  onToggleExpand,
}) => {
  const total = parseFloat(order?.total_price || '0') || 0;
  const tip = parseFloat(order?.tip || '0') || 0;
  const taxAmount = computeOrderTax(order);
  const subtotal = computeOrderSubtotal(order);
  const { formattedDate, formattedTime } = formatDateAndTime(order?.date);
  const StatusIcon = getStatusIcon(order?.status);

  const itemsOnlySummary = (
    <OrderItemsWrap direction="vertical" size="middle">
      {order.items?.map((item) => (
        <ItemCard key={item?.id} size="small">
          <ItemCardInner>
            <span className="item-name">
              <FontAwesomeIcon icon={faBox} />
              {item?.item?.item || 'Unknown Item'}
            </span>
            <div className="item-details">
              <Tag color="blue">Qty: {item?.quantity}</Tag>
              <span>${addCommasInPrices(item?.itemPrice)}</span>
            </div>
          </ItemCardInner>
        </ItemCard>
      ))}
    </OrderItemsWrap>
  );

  const itemsPanel = (
    <OrderItemsWrap direction="vertical" size="middle">
      {order.items?.map((item) => (
        <ItemCard key={item?.id} size="small">
          <ItemCardInner>
            <span className="item-name">
              <FontAwesomeIcon icon={faBox} />
              {item?.item?.item || 'Unknown Item'}
            </span>
            <div className="item-details">
              <Tag color="blue">Qty: {item?.quantity}</Tag>
              <span>${addCommasInPrices(item?.itemPrice)}</span>
            </div>
          </ItemCardInner>
          {item?.orderItemCustomizations && item?.orderItemCustomizations?.length > 0 ? (
            <CustomizationsWrap>
              <div className="order-meta-text customization-label">
                Customizations:
              </div>
              {item?.orderItemCustomizations?.map((customization) => (
                <CustomizationRow key={customization.id}>
                  <span>
                    {customization.customization?.customization}: {customization.item?.item}
                  </span>
                  <Tag color="green">
                    +${addCommasInPrices(parseFloat(customization?.itemPrice || '0'))}
                  </Tag>
                </CustomizationRow>
              ))}
            </CustomizationsWrap>
          ) : (
            <EmptyCustomizationsWrap>
              No modifications to this item
            </EmptyCustomizationsWrap>
          )}
        </ItemCard>
      ))}
    </OrderItemsWrap>
  );

  return (
    <StyledOrderCard>
      <OrderHeaderRow>
        <OrderInfoLeft>
          <Space size="small" wrap align="center">
            <Tag color={getStatusColor(order?.status)} icon={<FontAwesomeIcon icon={StatusIcon} />}>
              {getStatusLabel(order?.status)}
            </Tag>
            <span className="order-meta-text">#{order.id}</span>
          </Space>
          <OrderMeta>
            <span className="order-meta-item">
              <FontAwesomeIcon icon={faUser} />
              <span className="order-meta-value">
                {order?.user_name || 'Unknown Customer'}
              </span>
            </span>
            <span className="order-meta-item">
              <FontAwesomeIcon icon={faPhone} />
              <span className="order-meta-text">
                <PhoneBlurred>{getPhoneBlurred(order?.user_phone)}</PhoneBlurred>
                {getPhoneUnBlurred(order?.user_phone)}
              </span>
            </span>
            <span className="order-meta-item">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span className="order-meta-text">{formattedDate}</span>
            </span>
            <span className="order-meta-item">
              <FontAwesomeIcon icon={faClock} />
              <span className="order-meta-text">{formattedTime}</span>
            </span>
          </OrderMeta>
        </OrderInfoLeft>
        <OrderActionsBlock>
          <div className="order-amounts-wrap">
            <OrderAmounts>
              <div className="amount-row">
              <span className="amount-label">Total</span>
              <AmountValueTotal className="amount-value">
                ${addCommasInPrices(total)}
              </AmountValueTotal>
            </div>
            <div className="amount-row">
              <span className="amount-label">Subtotal</span>
              <span className="amount-value">${addCommasInPrices(subtotal)}</span>
            </div>
            <div className="amount-row">
              <span className="amount-label">Tax</span>
              <span className="amount-value">${addCommasInPrices(taxAmount)}</span>
            </div>
            <div className="amount-row">
              <span className="amount-label">Tip</span>
              <span className="amount-value">${addCommasInPrices(tip)}</span>
            </div>
            </OrderAmounts>
          </div>
          <StyledButton
            type="primary"
            ghost
            icon={<FontAwesomeIcon icon={isExpanded ? faEye : faEdit} />}
            onClick={onToggleExpand}
          >
            {isExpanded ? 'Hide Details' : 'View Details'}
          </StyledButton>
        </OrderActionsBlock>
      </OrderHeaderRow>
      <Divider />
      <SectionTitle level={5}>
        Order Items
        {isExpanded && (
          <span style={{ fontSize: '12px', fontWeight: '400', color: '#8b949e', marginLeft: '8px' }}>
            (with customizations)
          </span>
        )}
      </SectionTitle>
      {isExpanded ? itemsPanel : itemsOnlySummary}
    </StyledOrderCard>
  );
};

export default OrderCard;
